import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFirstLoginDisclaimer, setShowFirstLoginDisclaimer] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = api.getToken();
    if (token) {
      try {
        const userData = await api.getMe();
        setUser(userData);
        
        // Check localStorage for first login flag
        const storedUser = localStorage.getItem('lumiveta_user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          if (parsed.first_login) {
            setShowFirstLoginDisclaimer(true);
            // Clear the first login flag
            localStorage.setItem('lumiveta_user', JSON.stringify({ ...parsed, first_login: false }));
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        api.logout();
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const data = await api.login(email, password);
    
    // If 2FA is required, return the response without setting user
    // The login page will handle the 2FA flow
    if (data.requires_2fa) {
      return data;
    }
    
    // Get full user data including usage from /me endpoint
    try {
      const fullUserData = await api.getMe();
      setUser(fullUserData);
      localStorage.setItem('lumiveta_user', JSON.stringify(fullUserData));
    } catch (error) {
      // Fallback to login response data
      setUser(data.user);
      localStorage.setItem('lumiveta_user', JSON.stringify(data.user));
    }
    
    if (data.user.first_login) {
      setShowFirstLoginDisclaimer(true);
    }
    
    return data;
  };

  const register = async (email, password, name, preferredLanguage = 'en') => {
    const data = await api.register(email, password, name, preferredLanguage);
    setUser(data.user);
    localStorage.setItem('lumiveta_user', JSON.stringify(data.user));
    setShowFirstLoginDisclaimer(true); // Always show on first registration
    return data;
  };

  const logout = () => {
    api.logout();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const dismissDisclaimer = () => {
    setShowFirstLoginDisclaimer(false);
  };

  // Subscription status checks - specifically check for 'trial' vs 'premium'
  const subscriptionStatus = user?.subscription_status || 'free';
  // Premium = users with status 'premium' OR 'active' (both are paid/active subscriptions)
  const isPaidPremium = subscriptionStatus === 'premium' || subscriptionStatus === 'active';
  // Trial = users with status 'trial' or 'trialing' (legacy)
  const isTrialing = subscriptionStatus === 'trial' || subscriptionStatus === 'trialing';
  // Valid subscription = trial OR premium OR active
  const hasValidSubscription = isTrialing || isPaidPremium;
  
  // HARD GATE: Can user access the app?
  // Use backend's can_access_app which checks subscription_end_date expiration
  // Admins ALWAYS can, others need valid non-expired subscription
  const canAccessApp = user?.can_access_app ?? (user?.is_admin || hasValidSubscription);
  const hasHadTrial = user?.has_had_trial || false;
  const subscriptionExpired = user?.subscription_expired || false;

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    showFirstLoginDisclaimer,
    dismissDisclaimer,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isPremium: isPaidPremium,  // Only true for PAID 'premium' users
    isTrialing,  // True for 'trial' users
    isPaidPremium,
    hasValidSubscription,
    canAccessApp,
    hasHadTrial,
    subscriptionExpired,
    subscriptionEndDate: user?.subscription_end_date || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
